// Módulos de comunicación: Bluetooth HC-05 / HC-06 y lector RFID RC522.
import { definePart, PITCH } from './registry.js';

export const BT = { w: 16, d: 37, pcbY: 1.6, t: 1.6 };

const terminalProp = { key: 'phone', label: 'Teléfono (app de terminal Bluetooth)', type: 'terminal', runtime: true };

function btPins(names, labels) {
  // Conector macho en el borde corto de abajo (+Z), pines hacia arriba.
  return names.map((name, i) => ({
    name,
    label: labels[name] || name,
    kind: name === 'GND' ? 'gnd' : name === 'VCC' ? 'power' : 'signal',
    x: (i - (names.length - 1) / 2) * PITCH,
    y: BT.pcbY + BT.t + 8.6,
    z: BT.d / 2 + 1.4,
  }));
}

const LABELS = {
  STATE: 'STATE (alto con teléfono conectado)', RXD: 'RXD (recibe: al TX del Arduino)', TXD: 'TXD (envía: al RX del Arduino)',
  GND: 'GND', VCC: 'VCC (3.6–6 V)', EN: 'EN / KEY (alto = modo AT a 38400)',
};

definePart({
  type: 'bluetooth-hc05',
  name: 'Bluetooth HC-05',
  category: 'comunicacion',
  description: 'Puerto serie inalámbrico. Lo que el Arduino manda por TX aparece en el «teléfono» del panel, y lo que escribes ahí le llega al Arduino. Con EN en alto responde comandos AT.',
  keywords: 'bluetooth hc-05 hc05 serial softwareserial inalambrico telefono app at',
  props: [
    { key: 'baud', label: 'Velocidad (modo datos)', type: 'select', default: '9600', options: ['9600', '19200', '38400', '57600', '115200'].map((v) => ({ value: v, label: v + ' baudios' })) },
    { key: 'paired', label: 'Teléfono conectado', type: 'boolean', default: true, runtime: true },
    terminalProp,
  ],
  pins: btPins(['STATE', 'RXD', 'TXD', 'GND', 'VCC', 'EN'], LABELS),
  footprint: { w: BT.w + 1, d: BT.d + 4, h: 12, z: 1 },
  help: 'Conexión típica con SoftwareSerial: TXD del módulo al pin RX del sketch y RXD al pin TX (en un módulo real, RXD lleva un divisor de voltaje a 3.3 V).',
});

definePart({
  type: 'bluetooth-hc06',
  name: 'Bluetooth HC-06',
  category: 'comunicacion',
  description: 'Versión esclava sencilla del HC-05 (4 pines). Sin teléfono conectado responde comandos AT como «AT» o «AT+NAMEmiRobot».',
  keywords: 'bluetooth hc-06 hc06 serial softwareserial inalambrico telefono',
  props: [
    { key: 'baud', label: 'Velocidad', type: 'select', default: '9600', options: ['9600', '19200', '38400', '57600', '115200'].map((v) => ({ value: v, label: v + ' baudios' })) },
    { key: 'paired', label: 'Teléfono conectado', type: 'boolean', default: true, runtime: true },
    terminalProp,
  ],
  pins: btPins(['VCC', 'GND', 'TXD', 'RXD'], LABELS),
  footprint: { w: BT.w + 1, d: BT.d + 4, h: 12, z: 1 },
});

export const RFID = { w: 40, d: 60, pcbY: 1.6, t: 1.6 };

/**
 * Tarjetas y etiquetas que se pueden acercar al lector (la simulación y el modelo 3D usan esta misma lista).
 * kind: 'classic' = MIFARE Classic 1K (UID de 4 bytes); 'ntag213' = NTAG213 (UID de 7 bytes, 45 páginas).
 * El orden es el de las capas al apilarse sobre la antena (de abajo hacia arriba).
 */
export const RFID_TAGS = {
  card: { name: 'Tarjeta blanca (MIFARE 1K)', kind: 'classic', uid: [0x9a, 0x3b, 0x5c, 0x1d], prop: 'nearCard' },
  tag: { name: 'Etiqueta NFC (NTAG213)', kind: 'ntag213', uid: [0x04, 0x5c, 0x2e, 0xa1, 0x9b, 0x3d, 0x80], prop: 'nearTag', text: 'Hola desde la etiqueta NTAG213' },
  fob: { name: 'Llavero azul (MIFARE 1K)', kind: 'classic', uid: [0x7e, 0x21, 0x0f, 0x44], prop: 'nearFob' },
};
export const RFID_ORDER = Object.keys(RFID_TAGS);
export const uidText = (uid) => uid.map((b) => b.toString(16).toUpperCase().padStart(2, '0')).join(' ');

const TAG_STATE = { idle: 'esperando', ready: 'en anticolisión', active: 'seleccionada', halt: 'dormida (HALT)', off: 'sin energía' };

definePart({
  type: 'rfid-rc522',
  name: 'Lector RFID RC522',
  category: 'comunicacion',
  description: 'Lee y escribe tarjetas y etiquetas de 13.56 MHz por SPI (librería MFRC522): UID, bloques de valor y bits de acceso (MIFARE Classic 1K) y páginas (NTAG213). Puedes acercar varias a la vez: haz clic en cada una.',
  keywords: 'rfid rc522 mfrc522 nfc tarjeta llavero etiqueta mifare ntag ntag213 ultralight spi acceso monedero anticolision',
  props: RFID_ORDER.map((id) => ({
    key: RFID_TAGS[id].prop,
    label: `${RFID_TAGS[id].name} sobre el lector · UID ${uidText(RFID_TAGS[id].uid)}`,
    type: 'boolean',
    default: false,
    runtime: true,
  })),
  toggle: (props, key) => ({ key, value: !props[key] }),
  status: (p, v) => {
    if (!v.on) return 'Sin alimentación: el módulo necesita 3.3 V en 3.3V y GND.';
    const near = RFID_ORDER.filter((id) => v.states && v.states[id]);
    const field = v.field ? 'Antena encendida' : 'Antena apagada (el campo se enciende con PCD_Init)';
    return `${field}. ${near.length ? 'En el campo: ' + near.map((id) => `${RFID_TAGS[id].name} — ${TAG_STATE[v.states[id]] || v.states[id]}`).join('; ') : 'Ninguna tarjeta cerca.'}`;
  },
  pins: ['SDA', 'SCK', 'MOSI', 'MISO', 'IRQ', 'GND', 'RST', '3.3V'].map((name, i) => ({
    name,
    label: { SDA: 'SDA (SS: selección SPI)', SCK: 'SCK (reloj SPI)', MOSI: 'MOSI', MISO: 'MISO', IRQ: 'IRQ (no se usa)', GND: 'GND', RST: 'RST', '3.3V': '3.3 V' }[name],
    kind: name === 'GND' ? 'gnd' : name === '3.3V' ? 'power' : 'signal',
    x: (i - 3.5) * PITCH,
    y: RFID.pcbY + RFID.t + 8.6,
    z: RFID.d / 2 - 2.2,
  })),
  footprint: { w: 163, d: 76, h: 14, x: 30, z: 4.5 }, // la placa y las tarjetas sobre la mesa (a la derecha y a la izquierda)
  help: 'Conexión al Uno: SDA → 10, SCK → 13, MOSI → 11, MISO → 12, RST → 9, 3.3V y GND. En el Mega el SPI está en 50–53. ' +
    'Haz clic en una tarjeta (o marca su casilla) para acercarla al lector; con varias a la vez la librería las separa por anticolisión: ' +
    'PICC_IsNewCardPresent y PICC_ReadCardSerial eligen una, PICC_HaltA la duerme y se lee la siguiente. ' +
    'La tarjeta blanca y el llavero son MIFARE Classic 1K como las de NXP (llaves de fábrica FF…FF, bits de acceso y bloques de valor): ' +
    'con los bits de acceso de fábrica la llave B es legible (son datos) y, según la hoja de datos, no sirve para autenticar, así que se entra con la llave A ' +
    '(el ejemplo ReadAndWrite de la librería usa la B y falla; algunas tarjetas «clon» sí la aceptan). ' +
    'La etiqueta es una NTAG213 (UID de 7 bytes, 45 páginas, mensaje de texto NDEF). Cada una conserva su memoria mientras corre la simulación. ' +
    'No se calcula la criptografía Crypto1 (se compara la llave) ni se simulan READ_SIG, READ_CNT ni las tarjetas con UID modificable.',
});
